import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CalendarCheck, Mail, User, ChevronDown, X } from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });

const TABS = ["Upcoming", "Today", "Past", "Cancelled"];

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("Upcoming");
  const [expanded, setExpanded] = useState(null);

  const load = async () => setBookings((await api.get("/bookings")).data);
  useEffect(() => { load(); }, []);

  const t = todayStr();
  const filtered = bookings.filter((b) => {
    if (tab === "Cancelled") return b.status === "cancelled";
    if (b.status === "cancelled") return false;
    if (tab === "Today") return b.date === t;
    if (tab === "Upcoming") return b.date >= t;
    if (tab === "Past") return b.date < t;
    return true;
  });

  const cancel = async (b) => {
    if (!window.confirm("Cancel this booking?")) return;
    await api.delete(`/bookings/${b.booking_id}`);
    toast.success("Booking cancelled");
    load();
  };

  return (
    <div className="space-y-8" data-testid="bookings-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Bookings</h1>
        <p className="mt-1 text-slate-600">Everyone who booked your optimal hours.</p>
      </div>

      <div className="flex items-center gap-2 border-b border-slate-200">
        {TABS.map((tb) => (
          <button key={tb} onClick={() => setTab(tb)} data-testid={`bookings-tab-${tb.toLowerCase()}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === tb ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>{tb}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <CalendarCheck className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-sm">No {tab.toLowerCase()} bookings.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((b) => (
            <div key={b.booking_id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={`booking-${b.booking_id}`}>
              <div className="p-5 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <div className="text-center bg-blue-50 rounded-lg px-3 py-2 min-w-[64px]">
                    <p className="text-xs font-semibold text-blue-700 uppercase">{new Date(b.date + "T00:00:00").toLocaleDateString(undefined, { month: "short" })}</p>
                    <p className="text-xl font-extrabold text-blue-800">{new Date(b.date + "T00:00:00").getDate()}</p>
                  </div>
                  <div>
                    <p className="font-semibold">{b.template_name}</p>
                    <p className="text-sm text-slate-500">{fmtDate(b.date)} · {b.start_time}–{b.end_time}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" /> {b.invitee_name}</span>
                      <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {b.invitee_email}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {Object.keys(b.answers || {}).length > 0 && (
                    <button onClick={() => setExpanded(expanded === b.booking_id ? null : b.booking_id)} data-testid={`toggle-answers-${b.booking_id}`}
                      className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700">
                      Answers <ChevronDown className={`w-4 h-4 transition-transform ${expanded === b.booking_id ? "rotate-180" : ""}`} />
                    </button>
                  )}
                  {b.status === "confirmed" && (
                    <button onClick={() => cancel(b)} data-testid={`cancel-${b.booking_id}`}
                      className="inline-flex items-center gap-1 text-sm font-medium text-red-600 hover:text-red-700"><X className="w-4 h-4" /> Cancel</button>
                  )}
                  {b.status === "cancelled" && <span className="text-xs font-semibold text-slate-400 uppercase">Cancelled</span>}
                </div>
              </div>
              {expanded === b.booking_id && (
                <div className="border-t border-slate-100 bg-slate-50 px-5 py-4 space-y-2">
                  {Object.entries(b.answers).map(([k, v]) => (
                    <div key={k} className="text-sm"><span className="text-slate-500">{k}:</span> <span className="font-medium text-slate-800">{v}</span></div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
